import {
  ChatMessage,
  ComplianceResult,
  Character,
  Dogma,
  GenerateVideoParams,
  GenerationMode,
  ImageFile,
  PromptSequence,
  PromptSequenceStatus,
  RegeneratePromptParams,
  ReviseFollowingPromptsParams,
  Storyboard,
  VeoModel,
  VideoFile,
  VideoProvider,
} from '../types';
import { getGeminiKey } from '../utils/runtimeKeys';
import { compressBase64Image } from '../utils/imageCompression';
import {
  isSupabaseConfigured,
  // uploadVideoToSupabase, // Removed in favor of VideoStorageProvider
  uploadImageToSupabase
} from './supabaseClient';
import { VideoStorageFactory } from './VideoStorageProvider';
import { SupabaseVideoStorage } from './storage/SupabaseVideoStorage';
import { GoogleDriveVideoStorage } from './storage/GoogleDriveVideoStorage';
import { FalDebugService } from './falDebugService';
import * as ltxService from './ltxFrontendService';

// Initialize Storage Providers
// Verify we are not re-registering on hot reloads if possible, or Factory handles overwrites
// Initialize Storage Providers
VideoStorageFactory.register(new SupabaseVideoStorage());
VideoStorageFactory.register(new GoogleDriveVideoStorage());
// Drive is preferred if available (isAvailable check handles logic)

// ===========================================
// IMAGE COMPRESSION (to reduce payload size for Vercel 4.5MB limit)
// ===========================================

/**
 * Compress a base64-encoded image to reduce payload size.
 * Resizes to max dimension and compresses to JPEG.
 */
// Local compression function removed in favor of shared utility


// ===========================================
// Reference: https://ai.google.dev/gemini-api/docs/models

const MODELS = {
  // Nano Banana Pro - Latest reasoning model
  PRO: 'gemini-3-pro-preview',

  // Gemini 2.5 Flash - Fast and efficient
  FLASH: 'gemini-2.5-flash',

  // Nano Banana Pro - High-fidelity image generation
  IMAGE_PRO: 'gemini-3-pro-image-preview',

  // Gemini 2.5 Flash Image (Nano Banana) - Fast image generation/editing
  IMAGE_FLASH: 'gemini-2.5-flash-image',

  // TTS Preview
  TTS: 'gemini-2.5-flash-preview-tts',
};

// ===========================================
// IN-MEMORY KEY VAULT (Strict BYOK)
// ===========================================
let runtimeApiKey: string | null = null;

export const setRuntimeApiKey = (key: string | null) => {
  if (key) {
    runtimeApiKey = key.trim();
    // SECURITY: Don't log key length or any key-related info
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem('gemini_api_key', runtimeApiKey);
    }
  } else {
    runtimeApiKey = null;
    // Clear from sessionStorage too
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('gemini_api_key');
      // Also clear from preview storage if needed
      // Note: We don't import clearGeminiKey here to avoid circular dependencies if we don't have to,
      // but strictly speaking setRuntimeApiKey is memory-only setter.
      // The Dialog handles the persistence clearing.
    }
  }
};

export const getRuntimeApiKey = (): string | null => {
  return runtimeApiKey || getGeminiKey();
};

// ===========================================
// API KEY MANAGEMENT (Dual Mode: Server-Managed + BYOK)
// ===========================================

// Cached config to avoid repeated /api/config calls
let cachedConfig: { hasServerKey: boolean; requiresUserKey: boolean } | null = null;

/**
 * Fetch server configuration to determine API key mode
 * - hasServerKey: true if server has GEMINI_API_KEY env configured
 * - requiresUserKey: true if user must provide their own key (BYOK mode)
 */
export const fetchGeminiConfig = async (): Promise<{ hasServerKey: boolean; requiresUserKey: boolean }> => {
  if (cachedConfig) return cachedConfig;

  try {
    const res = await fetch('/api/config');
    if (!res.ok) {
      // Fallback to BYOK mode if config endpoint fails
      cachedConfig = { hasServerKey: false, requiresUserKey: true };
      return cachedConfig;
    }
    cachedConfig = await res.json();
    return cachedConfig;
  } catch {
    // Network error - fallback to BYOK mode
    cachedConfig = { hasServerKey: false, requiresUserKey: true };
    return cachedConfig;
  }
};

/**
 * Reset cached config (useful for testing or after configuration changes)
 */
export const resetConfigCache = () => {
  cachedConfig = null;
};

/**
 * Get API key from localStorage (BYOK mode)
 * Returns null if not set or invalid
 */
export const getLocalApiKey = (): string | null => {
  // BYOK Strict: Never read from storage.
  // The key must be passed from React State (Memory).
  return null;
};

/**
 * Save API key to BOTH localStorage and sessionStorage (unified BYOK mode)
 * localStorage = persistent across sessions
 * sessionStorage = available for Nano service within session
 */
export const setLocalApiKey = (key: string): void => {
  // BYOK Strict: Do not write to storage.
  // Just log for debugging (don't log the key).
  // SECURITY: Don't log anything about key handling
};

/**
 * Remove API key from BOTH localStorage and sessionStorage
 */
export const clearLocalApiKey = (): void => {
  // BYOK Strict: Nothing to clear from storage using this function.
  // React State clearing is handled by the UI component.
  if (typeof window !== 'undefined') {
    window.localStorage.removeItem('gemini_api_key');
    window.sessionStorage.removeItem('gemini_api_key');
  }
};

/**
 * Sync API key from localStorage to sessionStorage at boot
 * DEPRECATED: BYOK Strict Mode (Memory Only)
 */
export const syncApiKeyToSession = (): boolean => {
  // BYOK Strict: Do not restore from localStorage
  return false;
};


// Legacy exports for backward compatibility
export const getApiKey = (): string => {
  return getLocalApiKey() || '';
};

export const hasCustomApiKey = (): boolean => {
  const key = getLocalApiKey();
  return !!(key && key.startsWith('AIza'));
};

// ===========================================
// API CALL HELPERS
// ===========================================

const API_BASE = '/api';
const GOOGLE_FILES_API = 'https://generativelanguage.googleapis.com/upload/v1beta/files';

/**
 * Custom error type for API calls
 */
export interface ApiError {
  status: number;
  error: string;
  data?: any;
}

/**
 * Call backend endpoint with proper API key handling
 * - If server has key configured, no header needed
 * - If BYOK mode, adds x-api-key header from localStorage
 */
export const callVeoBackend = async (path: string, body: any): Promise<any> => {
  const config = await fetchGeminiConfig();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  // Only add user key header if server doesn't have a key configured
  if (!config.hasServerKey) {
    // Priority: Runtime Vault -> Legacy (null in Strict Mode)
    const key = getRuntimeApiKey() || getLocalApiKey();
    if (key) {
      headers['x-api-key'] = key;
    }
  }

  const res = await fetch(path, {
    method: 'POST',
    headers,
    body: JSON.stringify(body)
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const apiError: ApiError = {
      status: res.status,
      error: data.error || 'UNKNOWN_ERROR',
      data
    };
    throw apiError;
  }

  return data;
};

// Centralized API call with Strict BYOK logic
export const apiCall = async (endpoint: string, body: any, apiKey?: string) => {
  const config = await fetchGeminiConfig();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (!config.hasServerKey) {
    // Priority: Explicit -> Runtime -> Local (Legacy)
    const key = apiKey || getRuntimeApiKey() || getLocalApiKey();
    if (!key) {
      console.warn('[BYOK] Missing API Key! Runtime key present:', !!runtimeApiKey);
      const error = new Error('API_KEY_MISSING: Please enter your Gemini API key first') as any;
      error.code = 'API_KEY_MISSING';
      throw error;
    }
    headers['x-api-key'] = key;
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    // Try to parse JSON error, fallback to text
    let errorInfo;
    try {
      errorInfo = await res.json();
    } catch {
      errorInfo = { error: res.statusText };
    }

    // Check for "debug" field from our defensive backend
    const debugInfo = errorInfo.debug ? ` (Backend Status: ${errorInfo.debug.status}, Code: ${errorInfo.debug.code})` : '';

    const error = new Error(`API Request Failed: ${res.status}${debugInfo}`) as any;
    error.status = res.status;
    throw error;
  }
  return res.json();
};

// Streaming API call with SSE for progressive display
export const apiCallStream = async (
  endpoint: string,
  body: any,
  onChunk: (text: string) => void,
  apiKey?: string
): Promise<string> => {
  const config = await fetchGeminiConfig();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (!config.hasServerKey) {
    const key = apiKey || getRuntimeApiKey() || getLocalApiKey();
    if (!key) {
      throw new Error('API_KEY_MISSING: Please enter your Gemini API key first');
    }
    headers['x-api-key'] = key;
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const errorInfo = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(`API Request Failed: ${res.status} - ${errorInfo.error || 'Unknown'}`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error('Streaming not supported');

  const decoder = new TextDecoder();
  let fullText = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    const lines = chunk.split('\n');

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6));
          if (data.error) throw new Error(data.error);
          if (data.text) {
            fullText += data.text;
            onChunk(data.text);
          }
        } catch (e) {
          // Ignore parse errors for incomplete chunks
        }
      }
    }
  }

  return fullText;
};

// Helper to extract text from various response formats
const extractText = (response: any): string => {
  if (typeof response === 'string') return response;
  if (response.text) return response.text;
  if (response.candidates?.[0]?.content?.parts?.[0]?.text) {
    return response.candidates[0].content.parts[0].text;
  }
  return '';
};

// ===========================================
// GOOGLE FILES API - Upload large files directly to Google
// Bypasses Vercel 4.5MB limit - supports up to 2GB per file
// ===========================================

interface GoogleFileUploadResult {
  fileUri: string;
  mimeType: string;
  displayName: string;
}

/**
 * Upload a file to Google Files API via backend proxy
 * SECURITY: API key is handled server-side, never sent from browser
 * @param file - File or Blob to upload
 * @param displayName - Optional display name for the file
 * @returns fileUri that can be used with Gemini/Veo APIs
 */
/**
 * Upload a file to Google Files API via backend proxy
 * SECURITY: Backend initiates upload (API key hidden). Frontend uploads bytes directly.
 * LIMITATION: Bypasses Vercel 4.5MB limit (supports up to 2GB).
 */
export const uploadToGoogleFiles = async (
  file: File | Blob,
  displayName?: string
): Promise<GoogleFileUploadResult> => {
  const mimeType = file instanceof File ? file.type : 'application/octet-stream';
  const fileName = displayName || (file instanceof File ? file.name : `upload-${Date.now()}`);
  const numBytes = file.size;

  console.log(`[GoogleFiles] Starting secure direct upload: ${fileName} (${(numBytes / 1024 / 1024).toFixed(2)} MB)`);

  // Step 1: Get Upload URL from backend (Secure Init)
  // We send only metadata, not the file itself
  const initResult = await callVeoBackend('/api/files/upload', {
    displayName: fileName,
    mimeType,
    fileSize: numBytes
  });

  if (!initResult.uploadUrl) {
    throw new Error('Upload init failed: No URL received');
  }

  console.log('[GoogleFiles] Got upload URL, uploading bytes directly to Google...');

  // Step 2: Upload bytes directly to Google (Bypass Vercel Limit)
  // No API key needed here, the URL is pre-signed/session-bound
  const uploadResponse = await fetch(initResult.uploadUrl, {
    method: 'POST',
    headers: {
      'Content-Length': numBytes.toString(),
      'X-Goog-Upload-Offset': '0',
      'X-Goog-Upload-Command': 'upload, finalize',
    },
    body: file,
  });

  if (!uploadResponse.ok) {
    const errorText = await uploadResponse.text();
    throw new Error(`Failed to upload content to Google: ${uploadResponse.status} - ${errorText}`);
  }

  const result = await uploadResponse.json();

  if (!result.file || !result.file.uri) {
    throw new Error('Upload failed: No file URI in final response');
  }

  console.log(`[GoogleFiles] Upload complete: ${result.file.uri}`);

  return {
    fileUri: result.file.uri,
    mimeType: mimeType,
    displayName: fileName,
  };
};

/**
 * Convert an ImageFile (with base64) to a Google Files URI
 * Use this for large images that would exceed Vercel's limit
 */
export const uploadImageFileToGoogle = async (
  imageFile: ImageFile
): Promise<GoogleFileUploadResult> => {
  // Convert base64 to Blob
  const response = await fetch(`data:${imageFile.file.type};base64,${imageFile.base64}`);
  const blob = await response.blob();

  return uploadToGoogleFiles(blob, imageFile.file.name);
};

/**
 * Helper to create a fileData part for API calls (instead of inlineData)
 */
export const createFileDataPart = (fileUri: string, mimeType: string) => ({
  fileData: {
    fileUri,
    mimeType,
  }
});

// Threshold for using Google Files API instead of inline base64
// Vercel has 4.5MB limit, we use 3MB threshold for safety margin
const LARGE_FILE_THRESHOLD = 3 * 1024 * 1024; // 3MB

/**
 * Smart image part creator - uses fileUri for large images, inlineData for small ones
 * This automatically handles the Vercel 4.5MB limit
 */
const createImagePart = async (imageFile: ImageFile): Promise<any> => {
  // Estimate base64 size (roughly 1.37x the binary size)
  const estimatedSize = imageFile.base64.length * 0.75;

  if (estimatedSize > LARGE_FILE_THRESHOLD) {
    // Large file: Upload to Google Files API first
    console.log(`[SmartUpload] Image ${imageFile.file.name} is large (${(estimatedSize / 1024 / 1024).toFixed(2)}MB), using Google Files API`);
    const uploadResult = await uploadImageFileToGoogle(imageFile);
    return {
      fileData: {
        fileUri: uploadResult.fileUri,
        mimeType: uploadResult.mimeType,
      }
    };
  } else {
    // Small file: Use inline base64
    return {
      inlineData: {
        data: imageFile.base64,
        mimeType: imageFile.file.type || 'image/jpeg'
      }
    };
  }
};

/**
 * Process multiple images, uploading large ones to Google Files API
 */
const createImageParts = async (images: ImageFile[]): Promise<any[]> => {
  return Promise.all(images.map(img => createImagePart(img)));
};

// ===========================================
// VIDEO GENERATION (VEO) - Using predictLongRunning API
// ===========================================

interface VeoStartResponse {
  operationName: string;
}

interface VeoStatusResponse {
  done: boolean;
  videoUri?: string;
  error?: string;
}

export const generateVideo = async (
  params: GenerateVideoParams,
  signal: AbortSignal,
  onProgress?: (status: string) => void,
  apiKey?: string
): Promise<{ objectUrl: string; blob: Blob; uri: string; video: any; supabaseUrl?: string; metadata?: { startFrameMode?: string; fallbackApplied?: boolean } }> => {

  // [PIPELINE VALIDATION] STRICT: Reject Blob URLs for Generation Source
  // The backend cannot fetch 'blob:' URLs. We must have a persistent HTTP URL.
  if (params.mode === GenerationMode.EXTEND_VIDEO && params.inputVideoObject?.uri?.startsWith('blob:')) {
    const errorMsg = 'CRITICAL PIPELINE ERROR: Attempting to generate video from a local Blob URL. The video must be uploaded to Cloud Storage (Supabase/Drive) first.';
    console.error(errorMsg, params.inputVideoObject);
    throw new Error(errorMsg);
  }

  // === LTX-2 INTEGRATION ===
  if (params.provider === VideoProvider.LTX) {
    console.log('[GeminiService] Routing to LTX-2 Provider');
    onProgress?.('Initializing LTX-2...');

    // Determine mode based on generation params
    let ltxMode: 't2v' | 'i2v' | 'v2v' | 'keyframe' = 't2v';
    if (params.mode === GenerationMode.FRAMES_TO_VIDEO && params.startFrame) {
      ltxMode = 'i2v';
    } else if (params.mode === GenerationMode.EXTEND_VIDEO && params.inputVideoObject) {
      ltxMode = 'v2v';
    } else if (params.ltxParams?.keyframes?.length) {
      ltxMode = 'keyframe';
    }

    const ltxReq: ltxService.LTXGenerateRequest = {
      prompt: params.prompt,
      mode: params.ltxParams?.mode || ltxMode,
      model: params.ltxParams?.model || 'ltx-2-pro',
      image: params.startFrame?.base64 || undefined,
      video: params.inputVideoObject?.uri || undefined,
      keyframes: params.ltxParams?.keyframes,
      apiKey: params.ltxApiKey,
      parameters: params.ltxParams ? {
        // Core
        guidance_scale: params.ltxParams.guidance_scale,
        num_inference_steps: params.ltxParams.num_inference_steps,
        seed: params.ltxParams.seed,
        negative_prompt: params.ltxParams.negative_prompt,

        // Output specs
        duration_sec: params.ltxParams.duration_sec,
        resolution: params.ltxParams.resolution,
        fps: params.ltxParams.fps,
        aspect_ratio: params.ltxParams.aspect_ratio,

        // Audio (LTX-2 exclusive)
        generate_audio: params.ltxParams.generate_audio,
        audio_prompt: params.ltxParams.audio_prompt,

        // Camera motion
        camera_motion: params.ltxParams.camera_motion,

        // Control models (IC-LoRA)
        control_type: params.ltxParams.control_type,
        control_image: params.ltxParams.control_image,
        control_strength: params.ltxParams.control_strength,

        // Enhancement
        enhance_prompt: params.ltxParams.enhance_prompt,

        // Retake mode
        retake_source_video: params.ltxParams.retake_source_video,
        retake_preserve: params.ltxParams.retake_preserve,
      } : undefined
    };

    // [FAL PROOF MODE] Start Job
    const falJobId = FalDebugService.startJob('ltx2_fal', params.ltxParams?.model || 'ltx-2-pro', { ...params.ltxParams, prompt: params.prompt });

    try {
      FalDebugService.logRequest(falJobId, 'fal-ai/ltx-video', ltxReq);
      onProgress?.(`Generating with LTX-2 ${params.ltxParams?.model || 'Pro'}...`);

      FalDebugService.logStatus(falJobId, 'polling');
      const res = await ltxService.generateVideo(ltxReq);

      if (!res.success) {
        if (res.error === 'MODEL_LOADING') {
          throw new Error(`LTX Model Loading (${Math.round(res.estimated_time || 0)}s estimated). Please try again shortly.`);
        }
        throw new Error(res.error || 'LTX generation failed');
      }

      onProgress?.('Downloading LTX video...');
      FalDebugService.logStatus(falJobId, 'downloading');

      // Convert URL to Blob (support base64 data URIs or http URLs)
      const videoResponse = await fetch(res.videoUrl);
      const videoBlob = await videoResponse.blob();
      const objectUrl = URL.createObjectURL(videoBlob);

      console.log('[GeminiService] LTX-2 Success:', res.metadata);

      const result = {
        objectUrl,
        blob: videoBlob,
        uri: res.videoUrl,
        video: { uri: res.videoUrl },
        supabaseUrl: res.videoUrl.startsWith('http') ? res.videoUrl : undefined
      };

      FalDebugService.logResponse(falJobId, res, res.videoUrl);
      return result;
    } catch (err) {
      console.error('[GeminiService] LTX Generation Error:', err);
      throw err;
    }
  }

  // === KLING INTEGRATION ===
  if (params.provider === VideoProvider.KLING) {
    console.log('[GeminiService] Routing to Kling Provider');
    onProgress?.('Initializing Kling 2.6...');

    const { generateVideo: generateKling } = await import('./klingFrontendService');

    try {
      const res = await generateKling({
        prompt: params.prompt,
        image_url: params.startFrame ? `data:image/jpeg;base64,${params.startFrame.base64}` : undefined,
        aspect_ratio: params.aspectRatio === '9:16' ? '9:16' : '16:9',
        duration: '5', // Default to 5s for now
        apiKey: apiKey
      });

      onProgress?.('Downloading Kling video...');
      const videoResponse = await fetch(res.videoUrl);
      const videoBlob = await videoResponse.blob();
      const objectUrl = URL.createObjectURL(videoBlob);

      return {
        objectUrl,
        blob: videoBlob,
        uri: res.videoUrl,
        video: { uri: res.videoUrl },
        supabaseUrl: res.videoUrl // optimize upload later
      };
    } catch (err: any) {
      console.error('[GeminiService] Kling Error:', err);
      throw err;
    }
  }

  // Determine if this is an extension with a valid video reference
  const videoUri = params.mode === GenerationMode.EXTEND_VIDEO && params.inputVideoObject?.uri
    ? params.inputVideoObject.uri
    : undefined;

  // Clear logging for sequence debugging
  if (videoUri) {
    console.log(`[Sequence/Extend] Generating extension with baseVideo=${videoUri}`);
  } else if (params.mode === GenerationMode.EXTEND_VIDEO) {
    console.warn('[Sequence/Extend] WARNING: EXTEND_VIDEO mode but no baseVideo URI! Will generate as text-to-video instead of true extension.');
  } else {
    console.log(`[Sequence] Generating root shot with mode=${params.mode}`);
  }
  console.log('[Veo] Starting video generation...', params);

  // P0.7: Check apiKey param first
  const key = apiKey || getApiKey();
  if (!key) {
    throw new Error('API_KEY_MISSING: Please enter your Gemini API key first');
  }

  try {
    // Build parameters for the API
    const parameters: Record<string, any> = {};

    if (params.mode !== GenerationMode.REFERENCES_TO_VIDEO) {
      if (params.resolution) {
        parameters.resolution = params.resolution;
      }
    }

    if (params.mode !== GenerationMode.EXTEND_VIDEO &&
      params.mode !== GenerationMode.REFERENCES_TO_VIDEO) {
      if (params.aspectRatio) {
        parameters.aspectRatio = params.aspectRatio;
      }
    }

    // Build prompt with any necessary instructions
    let finalPrompt = params.prompt;

    if (params.mode === GenerationMode.FRAMES_TO_VIDEO && params.startFrame) {
      const instruction =
        'CRITICAL INSTRUCTION: You are to animate the provided image, NOT reinterpret it. The very first frame of the video MUST be pixel-perfect identical to the provided start image. DO NOT add, remove, or change any characters, objects, or environmental elements present in the image. The composition is fixed. Your only task is to create motion, animating ONLY what is already there.\n\n';
      finalPrompt = instruction + finalPrompt;
    }

    if (!finalPrompt.trim()) {
      if (params.mode === GenerationMode.EXTEND_VIDEO) {
        finalPrompt = 'Continue the scene.';
      } else {
        throw new Error('A prompt description is required.');
      }
    }

    // 1. Start Generation using /api/video/generate endpoint
    onProgress?.('Starting video generation...');
    console.log('[Veo] Calling /api/video/generate...', {
      hasVideoUri: !!videoUri,
      hasStartFrame: !!params.startFrame,
      mode: params.mode
    });

    // Compress startFrame if present to avoid 413 Payload Too Large (Vercel limit ~4.5MB)
    // If still too large after compression, upload to Supabase and use URL
    let startFrameBase64 = params.startFrame?.base64;
    let startFrameUrl: string | undefined = undefined;
    let startFrameDriveId: string | undefined = undefined;

    if (startFrameBase64) {
      try {
        const originalSize = startFrameBase64.length;
        // Check size first. LIMIT is 4.5MB. Safe threshold 4MB.
        // User requested NO COMPRESSION for master shots if possible.
        // Strategy:
        // 1. If < 4MB -> Send Original
        // 2. If > 4MB -> Upload to Drive (Preserve Quality)
        // 3. Last Resort -> Compress (but high quality)

        const startFrameSize = startFrameBase64.length;
        const VERCEL_SAFE_LIMIT = 4 * 1024 * 1024; // 4MB

        if (startFrameSize > VERCEL_SAFE_LIMIT) {
          console.log(`[Veo] StartFrame is large (${(startFrameSize / 1024 / 1024).toFixed(2)}MB). Uploading to Drive to preserve quality...`);

          // Dynamic import to avoid circular deps
          const { uploadLargeFileToDrive } = await import('./mediaUploadService');

          // UPLOAD TO DRIVE FOR PERSISTENCE (Supabase -> Drive -> Delete)
          const uploadResult = await uploadLargeFileToDrive(startFrameBase64, `startframe_${Date.now()}.png`);

          if (uploadResult.success) {
            startFrameUrl = uploadResult.url;
            startFrameDriveId = uploadResult.path; // path = fileId
            startFrameBase64 = undefined; // Don't send base64
          } else {
            console.warn('[Veo] Drive upload failed. Fallback to high-quality compression.');
            // Fallback: Compress but keep high resolution if possible
            startFrameBase64 = await compressBase64Image(startFrameBase64, {
              maxDimension: 2048,
              quality: 0.85,
              threshold: 0
            });
          }
        } else {
          console.log(`[Veo] StartFrame is safe size (${(startFrameSize / 1024 / 1024).toFixed(2)}MB). Sending original.`);
        }
      } catch (e) {
        console.warn('[Veo] Failed to process startFrame:', e);
      }
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Start Frame Mode Logic
    // ─────────────────────────────────────────────────────────────────────────
    const startFrameMode = params.startFrameMode || 'reference_soft';
    console.log('[Veo] startFrameMode:', startFrameMode);

    // If lock_first_frame AND we have a startFrame, log warning about potential fallback
    if (startFrameMode === 'lock_first_frame' && params.startFrame) {
      console.log('[Veo] WARNING: lock_first_frame mode enabled. If Veo ignores the image, fallback will be applied.');
    }

    const startResponse = await fetch(`${API_BASE}/video/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': key,
      },
      body: JSON.stringify({
        model: params.model,
        prompt: finalPrompt.trim(),
        parameters: Object.keys(parameters).length > 0 ? parameters : undefined,
        // Pass video URI for extend mode (Veo-generated videos)
        videoUri: videoUri,
        // Pass startFrame (Use Drive ID if available for persistence)
        startFrame: startFrameBase64,
        startFrameUrl: startFrameUrl,
        startFrameDriveId: startFrameDriveId,
        // Start Frame Mode for first frame control
        startFrameMode: startFrameMode,
      }),
      signal,
    });

    if (!startResponse.ok) {
      const errorData = await startResponse.json().catch(() => ({}));
      throw new Error(errorData.error || `Failed to start video generation: ${startResponse.statusText}`);
    }

    const startData: VeoStartResponse = await startResponse.json();
    const operationName = startData.operationName;
    console.log('[Veo] Operation started:', operationName);

    // 2. Poll until done
    let status: VeoStatusResponse = { done: false };
    let pollCount = 0;
    const maxPolls = 120; // 10 minutes max (5s * 120)

    while (!status.done && pollCount < maxPolls) {
      // Check if aborted
      if (signal.aborted) {
        throw new DOMException('Video generation cancelled', 'AbortError');
      }

      // Wait 5 seconds between polls
      await new Promise(resolve => setTimeout(resolve, 5000));

      pollCount++;
      onProgress?.(`Generating video... (${pollCount * 5}s elapsed)`);
      console.log(`[Veo] Polling... (${pollCount * 5}s elapsed)`);

      const statusResponse = await fetch(
        `${API_BASE}/video/status?name=${encodeURIComponent(operationName)}`,
        {
          method: 'GET',
          headers: {
            'x-api-key': key,
          },
          signal,
        }
      );

      if (!statusResponse.ok) {
        const errorData = await statusResponse.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to poll video status: ${statusResponse.statusText}`);
      }

      status = await statusResponse.json();

      if (status.error) {
        throw new Error(status.error);
      }
    }

    if (!status.done) {
      throw new Error('Video generation timed out after 10 minutes');
    }

    if (!status.videoUri) {
      throw new Error('No video URI in response');
    }

    console.log('[Veo] Video ready:', status.videoUri);

    // 3. Download video via proxy
    onProgress?.('Downloading video...');
    console.log('[Veo] Downloading video via proxy...');

    const downloadResponse = await fetch(
      `${API_BASE}/proxy-video?uri=${encodeURIComponent(status.videoUri)}`,
      {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
        },
        signal,
      }
    );

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download video: ${downloadResponse.statusText}`);
    }

    let videoBlob = await downloadResponse.blob();

    console.log('[Veo] Video downloaded:', videoBlob.size, 'bytes');

    // ─────────────────────────────────────────────────────────────────────────
    // 3.5 LOCK_FIRST_FRAME Fallback - Verify and prepend if needed
    // ─────────────────────────────────────────────────────────────────────────
    let fallbackApplied = false;
    if (startFrameMode === 'lock_first_frame' && params.startFrame?.base64) {
      try {
        onProgress?.('Verifying first frame lock...');
        console.log('[Veo] lock_first_frame mode: Verifying first frame matches Nano Pro image...');

        // Dynamic import to avoid increasing initial bundle size
        const { ensureFirstFrameLock } = await import('./videoPostProcessor');

        const result = await ensureFirstFrameLock(videoBlob, params.startFrame.base64, {
          similarityThreshold: 0.85,
          stillDurationMs: 300,
          skipVerification: false // Set true to always prepend without checking
        });

        videoBlob = result.video;
        fallbackApplied = result.fallbackApplied;

        if (fallbackApplied) {
          console.log(`[Veo] FALLBACK APPLIED: First frame didn't match (score: ${result.similarityScore?.toFixed(3)}). Still image prepended.`);
          onProgress?.('Fallback applied: still frame prepended');
        } else {
          console.log(`[Veo] First frame verified OK (score: ${result.similarityScore?.toFixed(3)})`);
        }
      } catch (postProcessError) {
        console.error('[Veo] Post-processing error (non-fatal):', postProcessError);
        // Continue with original video if post-processing fails
      }
    }

    const objectUrl = URL.createObjectURL(videoBlob);

    // 4. Optional: Upload to Supabase if configured (via Provider)
    let supabaseUrl: string | undefined;

    // Use the Storage Factory to get the best available provider
    const storageProvider = await VideoStorageFactory.getAvailableProvider();

    if (storageProvider) {
      try {
        onProgress?.(`Uploading to storage (${storageProvider.name})...`);
        console.log(`[Veo] Uploading to ${storageProvider.name}...`);

        const filename = `veo-${params.model}-${Date.now()}.mp4`;
        const result = await storageProvider.upload(videoBlob, filename, {
          contentType: 'video/mp4',
          public: true
        });

        supabaseUrl = result.publicUrl;
        console.log(`[Veo] Video uploaded to ${storageProvider.name}:`, supabaseUrl);
      } catch (uploadError) {
        console.warn('[Veo] Failed to upload to storage provider:', uploadError);
        // Don't crash the generation if upload fails, just log it
      }
    } else {
      console.log('[Veo] No storage provider available, skipping upload.');
    }

    return {
      objectUrl,
      blob: videoBlob,
      uri: status.videoUri,
      video: { uri: status.videoUri },
      supabaseUrl,
      // Metadata for lock_first_frame mode
      metadata: {
        startFrameMode,
        fallbackApplied
      }
    };

  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    console.error('[Veo] Video Generation Error:', error);
    throw error;
  }
};

// ===========================================
// TEXT / CHAT GENERATION
// ===========================================

export const generatePromptFromImage = async (image: ImageFile, apiKey?: string): Promise<string> => {
  // Use smart upload for large images
  const imagePart = await createImagePart(image);

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: {
      parts: [
        imagePart,
        { text: 'Describe this image in a creative and detailed way to be used as a prompt for a video generation model. Focus on action, mood, and visual details. The description should be a single, cohesive paragraph.' }
      ]
    }
  }, apiKey);
  return extractText(response).trim();
};

export const generatePromptSequence = async (
  sceneDescription: string,
  totalDuration: number,
  dogma: Dogma | null,
  masterShot: ImageFile | null,
  apiKey?: string,
  videoProvider?: VideoProvider
): Promise<PromptSequence> => {
  const isLTX = videoProvider === VideoProvider.LTX;
  const mainLimit = isLTX ? 20 : 8;
  const remainingDuration = totalDuration - mainLimit;
  const averageExtensionDuration = isLTX ? 15 : 5.5;
  const numExtensions = remainingDuration > 0 ? Math.ceil(remainingDuration / averageExtensionDuration) : 0;
  const dogmaText = dogma?.text ?? '';

  const systemPrompt = `You are a MASTER CINEMATIC DIRECTOR. Your goal is to break down a scene into a ${isLTX ? 'LTX-2' : 'VEO'} VIDEO GENERATION sequence.
  
  CRITICAL INSTRUCTION: You must think in terms of CAMERA ANGLES and SHOT SIZES (Découpage technique).
  If the user does not specify camera details, you MUST INVENT THEM to create a dynamic sequence.
  
  ENGINE CONTEXT:
  - Provider: ${isLTX ? 'LTX-2 (fal.ai)' : 'Veo (Google)'}
  - Main Shot Max Duration: ${mainLimit}s
  - Extensions Max Duration: ${isLTX ? '20s' : '8s'}
  - For ${isLTX ? 'LTX-2' : 'Veo'}, optimize descriptors for high fidelity.
  
  DOGMA / STYLE REFERENCE:
  - If Dogma reference images are provided, they represent the VISUAL DNA (lighting, color, texture, mood).
  - DO NOT mention or include specific subjects, objects, or characters present in the DOGMA images unless they are part of the user's current request.
  - The DOGMA is for STYLE ONLY. Do not animate the style reference.

  MASTER SHOT CONTEXT:
  - If a MASTER SHOT image is provided, ALL generated shots must be visual derivatives of this scene.
  - Maintain the same lighting, color palette, and environmental details as the Master Shot.
  - Treat the Master Shot as the "establishing shot" or reference implementation.
  
  GUIDELINES:
  - Start with an ESTABLISHING SHOT (Wide/Extreme Wide) to set the scene geometry.
  - Move to MEDIUM/CLOSE shots for character action/emotion.
  - Use INSERTS for details.
  - Mention CAMERA HEIGHT (Low angle for power/scale, High angle for vulnerability/overview).
  
  STRUCTURE:
  1. Main Prompt (${mainLimit}s): Introduce the scene and subjects. BE SPECIFIC about the setting.
  2. ${numExtensions} Extension Prompts (${isLTX ? '20' : '4-7'}s each): Continue the action. ONE key action per shot.
  
  DOGMA constraints: ${dogmaText}
  
  JSON OUTPUT ONLY: { "mainPrompt": "string", "extensionPrompts": ["string"] }`;

  const parts: any[] = [];

  // 1. Add Dogma Reference Images
  if (dogma?.referenceImages) {
    for (const img of dogma.referenceImages) {
      parts.push({ inlineData: { data: img.base64, mimeType: img.type } });
    }
  }

  // 2. Add Master Shot (if provided) - This is critical for Decoupage mode
  if (masterShot) {
    parts.push({ inlineData: { data: masterShot.base64, mimeType: masterShot.file.type || 'image/jpeg' } });
    parts.push({ text: "Use the above image as the MASTER SHOT reference for visual consistency." });
  }

  parts.push({ text: `Scene: "${sceneDescription}".Duration: ${totalDuration} s.` });

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: { parts },
    config: {
      systemInstruction: systemPrompt,
      responseMimeType: 'application/json'
    }
  }, apiKey);

  const text = extractText(response);
  try {
    const parsed = JSON.parse(text);
    // Return full PromptSequence structure with defaults for new fields
    const sequence: PromptSequence = {
      id: crypto.randomUUID(),
      dogmaId: dogma?.id ?? null,
      mainPrompt: parsed.mainPrompt,
      extensionPrompts: parsed.extensionPrompts || [],
      status: PromptSequenceStatus.CLEAN,
      dirtyExtensions: [],
      createdAt: new Date().toISOString(),
    };
    return sequence;
  } catch {
    const match = text.match(/\{[\s\S]*\}/);
    if (match) {
      const parsed = JSON.parse(match[0]);
      return {
        id: crypto.randomUUID(),
        dogmaId: dogma?.id ?? null,
        mainPrompt: parsed.mainPrompt,
        extensionPrompts: parsed.extensionPrompts || [],
        status: PromptSequenceStatus.CLEAN,
        dirtyExtensions: [],
        createdAt: new Date().toISOString(),
      };
    }
    throw new Error('Failed to parse sequence response');
  }
};

// ===========================================
// AUTOMATIC MOTION ANALYSIS (Gemini Vision)
// ===========================================

/**
 * Automatically analyze motion/movement between two video frames using Gemini Vision.
 * Returns a concise description of direction, speed, and trajectory.
 */
export const analyzeMotionBetweenFrames = async (
  firstFrame: ImageFile,
  lastFrame: ImageFile,
  apiKey?: string
): Promise<string> => {
  console.log('[MotionAnalysis] Analyzing motion between frames...');

  const systemPrompt = `You are a video continuity expert.Analyze the motion / movement between these two video frames(first frame and last frame of an 8 - second clip).

  Describe in 2 - 3 sentences:
  1. The DIRECTION of movement(left - to - right, upward, toward camera, etc.)
  2. The SPEED / INTENSITY(slow pan, fast action, static, etc.)
  3. Any notable CHANGES(zoom, rotation, subject movement)

Be specific and concise.Use cinematic terminology.Respond in the same language as any visible text, or French by default.`;

  try {
    // Compress images if needed to reduce payload
    let firstBase64 = firstFrame.base64;
    let lastBase64 = lastFrame.base64;

    if (firstBase64.length > 300 * 1024) {
      firstBase64 = await compressBase64Image(firstBase64);
    }
    if (lastBase64.length > 300 * 1024) {
      lastBase64 = await compressBase64Image(lastBase64);
    }

    const response = await apiCall('/generate-content', {
      model: MODELS.FLASH, // Use faster model for quick analysis
      contents: [{
        role: 'user',
        parts: [
          { text: 'First frame (start of video):' },
          { inlineData: { data: firstBase64, mimeType: 'image/jpeg' } },
          { text: 'Last frame (end of video):' },
          { inlineData: { data: lastBase64, mimeType: 'image/jpeg' } },
        ]
      }],
      config: { systemInstruction: systemPrompt }
    }, apiKey);

    const analysis = extractText(response);
    console.log('[MotionAnalysis] Result:', analysis);
    return analysis;
  } catch (error) {
    console.error('[MotionAnalysis] Failed:', error);
    return ''; // Return empty on error - user can still describe manually
  }
};

export const generateSequenceFromConversation = async (
  messages: ChatMessage[],
  dogma: Dogma | null,
  duration: number,
  extensionContext?: ImageFile | null,
  motionDescription?: string | null,
  apiKey?: string,
  // P1: Director Mode sequence intent
  sequenceIntent?: 'plan-sequence' | 'decoupage' | 'infinite-chain' | null,
  additionalContext?: string,
  mentionedCharacters?: Character[],
  // P2: Video Provider Context for LTX-2 vs Veo awareness
  videoProvider?: VideoProvider
): Promise<string | { creativePrompt: string; veoOptimizedPrompt: string }> => {

  // Build context instruction based on whether this is an extension
  let contextInstruction = '';
  if (extensionContext) {
    contextInstruction = `User wants to EXTEND a video.Last frame provided as visual anchor.`;
    if (motionDescription) {
      contextInstruction += `\nCONTINUITY CONTEXT(from video analysis): "${motionDescription}"
This describes the movement / direction from the original video that the extension should continue.`;
    }
  } else {
    contextInstruction = `User wants a NEW video.Duration: ${duration} s.`;
  }

  // P1: Director Mode context - adapt generation based on sequence type
  if (sequenceIntent === 'plan-sequence') {
    contextInstruction += `\n\n🎬 MODE RÉALISATEUR: PLAN - SÉQUENCE(caméra continue)
  - Générer UN SEUL prompt vidéo continu
    - Pas de coupes, transitions fluides
      - Le mouvement de caméra doit être cohérent du début à la fin
        - Durée totale: ${duration}s en un seul plan`;
  } else if (sequenceIntent === 'decoupage') {
    contextInstruction += `\n\n🎬 MODE RÉALISATEUR: DÉCOUPAGE(plans multiples)
    - Générer une SÉQUENCE de plans avec valeurs variées
      - Inclure: plan large(établissement), plan moyen(action), plan serré(émotion)
        - Chaque plan doit avoir une transition logique
          - Suggérer ~3 - 5 plans pour couvrir la scène`;
  }

  // DEBUG: Log dogma being used
  console.log('[DogmaDebug] generateSequenceFromConversation called with dogma:', {
    id: dogma?.id || 'none',
    title: dogma?.title || 'none',
    textPreview: dogma?.text?.substring(0, 100) + '...' || 'none'
  });

  // Count turns for reference (but not to force output)
  const userMessageCount = messages.filter(m => m.role === 'user').length;

  let systemInstruction = '';

  // === DÉCOUPAGE MODE: Strict JSON Shot List ===
  if (sequenceIntent === 'decoupage') {
    systemInstruction = `You are a Technical Director assistant creating a 'Découpage Technique' (Shot List).
Your goal is to break down the user's scene into a list of specific shots (3-6 shots typically).

CRITICAL OUTPUT RULES:
1. Output MUST be a valid JSON Array of objects.
2. NO markdown, NO text before/after. Just the JSON.
3. Use French camera terms for 'scale', 'angle', 'movement'.

Output Format (JSON Schema):
[
  {
    "tc_in": "HH:MM:SS:FF", // Start timecode (e.g. "00:00:00:00")
    "tc_out": "HH:MM:SS:FF", // End timecode (e.g. "00:00:03:00")
    "camera": {
      "scale": "plan_moyen", // Options: plan_ensemble, plan_large, plan_moyen, plan_americain, gros_plan, insert
      "angle": "neutre", // Options: neutre, plongee, contre_plongee
      "movement": "fixe", // Options: fixe, travelling_avant, travelling_lateral, panoramique, handheld, steadicam
      "lens_mm": 50,
      "locked": true
    },
    "action": "Brief description of the action in the shot",
    "prompt_notes": "Detailed visual description for Image Generation (Flux). Describe lighting, colors, subject appearance.",
    "characters": [ { "name": "Name" } ] // Must match available characters
  }
]

CONTEXT:
Dogma: ${dogma?.text || 'None'}
${additionalContext || ''}
`;
  }
  // === PLAN-SÉQUENCE / STANDARD MODE ===
  else {
    systemInstruction = `You are a creative VIDEO DIRECTOR assistant. Your role is to help the user craft the perfect video prompt.

    BEHAVIOR:
  1. REMEMBER everything the user has told you throughout the conversation. NEVER forget or ask again for information already provided.
2. Be HELPFUL and COLLABORATIVE. Ask clarifying questions when needed, but don't interrogate.
  3. RESPECT the user's creative vision. You are here to assist, not impose.
  4. When the user seems satisfied or says "generate", "ok", "go", produce the final JSON.

OUTPUT FORMAT (when user is ready):
\`\`\`json
{
  "creativePrompt": "Creative Brief describing the scene, mood, and artistic intent. You can use @Name mentions here.",
  "veoOptimizedPrompt": "Technical VEO prompt. CRITICAL: YOU MUST REPLACE ALL @Name MENTIONS WITH THEIR VISUAL DESCRIPTIONS. Do not use @Name in this field. Describe the person physically (e.g. 'a redhead woman in a spacesuit' instead of '@Sophie'). Include camera details, lighting, movement."
}
\`\`\`

RULES:
- Respond in the same language as the user.
- Keep responses concise but don't sacrifice helpfulness.
- If the user provides an image or reference, acknowledge it and use it.
- If user says "retouch" or gives a quick direction (e.g., "closer", "more light"), output JSON immediately.

CONTEXT:
- Dogma: ${dogma?.title || 'None'}${dogma?.text ? ` - ${dogma.text.substring(0, 200)}` : ''}
- ${contextInstruction}
${videoProvider === VideoProvider.LTX ? `
🎬 VIDEO ENGINE: LTX-2 (via fal.ai)
CAPABILITIES IMPORTANTES:
- Durée MAX par plan: 20 secondes (sans extension)
- Extensions: +20 secondes par extension (total 40s, 60s, etc.)
- Résolutions: 1080p (standard), 1440p, 4K
- FPS: 25fps (normal) ou 50fps (ralenti - max 10s)
- LoRA: Peut maintenir la cohérence des personnages/styles sur toute la durée
- Audio: Génération automatique d'audio synchronisé disponible

OPTIMISATIONS LTX-2:
- Pour des plans longs (>10s): Utiliser la durée native plutôt que des extensions
- Pour des ralentis: Spécifier 50fps, limité à 10 secondes
- Pour la cohérence personnage: Mentionner explicitement les caractéristiques à maintenir
- Le prompt peut être plus détaillé car LTX-2 gère bien les descriptions longues
` : `
🎬 VIDEO ENGINE: Veo (Google)
CAPABILITIES:
- Durée par plan: 8 secondes maximum
- Extensions: Nécessaires pour plans longs (8s + 8s + ...)
- Résolutions: 720p, 1080p
- Style: Cinématique, haute qualité
- Video-to-Video: Extension depuis dernière frame

OPTIMISATIONS VEO:
- Prompts concis et précis (limite de tokens)
- Décrire le mouvement de caméra clairement
- Pour >8s: Prévoir des extensions cohérentes
`}
${additionalContext ? `\nADDITIONAL CONTEXT & CHARACTERS:\n${additionalContext}` : ''}`;
  }


  // ═══════════════════════════════════════════════════════════════════
  // SANITIZE MESSAGES: Keep only text from history, use ONE visual anchor
  // ═══════════════════════════════════════════════════════════════════

  // Find the visual anchor: prefer extensionContext, else the last image in messages
  let visualAnchor: ImageFile | null = extensionContext || null;
  if (!visualAnchor) {
    // Find the most recent image in the conversation
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].image) {
        visualAnchor = messages[i].image;
        break;
      }
    }
  }

  // Build sanitized API contents
  // Initialize with empty array
  const apiContents: any[] = [];

  // Inject Mentioned Character Reference Images (Multimodal Context)
  if (mentionedCharacters && mentionedCharacters.length > 0) {
    console.log(`[GenContent] Injecting visual references for ${mentionedCharacters.length} characters`);
    for (const char of mentionedCharacters) {
      if (char.images && char.images.length > 0) {
        // Add a text header for the character
        apiContents.push({
          role: 'user',
          parts: [{ text: `REFERENCE CHARACTER: "${char.name}"\nDescription: ${char.description || 'See images.'}\nVisual References:` }]
        });

        for (const img of char.images) {
          try {
            // Reconstruct ImageFile-like object for createImagePart
            // Must convert base64 to Blob to get correct size for potential Google Files upload

            // COMPRESSION STEP: Reduce size before sending (Fix 413)
            const compressedBase64 = await compressBase64Image(img.base64);

            const res = await fetch(`data:${img.type};base64,${compressedBase64}`);
            const blob = await res.blob();
            const file = new File([blob], img.name, { type: img.type });
            const imgFile: ImageFile = { file, base64: compressedBase64 };

            const part = await createImagePart(imgFile);
            apiContents.push({
              role: 'user',
              parts: [
                { text: `[${char.name} - ${img.label || 'View'}]` },
                part
              ]
            });
          } catch (e) {
            console.warn(`[GenContent] Failed to attach reference image for ${char.name}`, e);
          }
        }
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // HISTORY LIMITING: Max 10 messages to reduce payload/latency
  // ═══════════════════════════════════════════════════════════════════
  const MAX_HISTORY = 10;
  let limitedMessages = messages;
  let historySummary = '';

  if (messages.length > MAX_HISTORY) {
    const oldMessages = messages.slice(0, -MAX_HISTORY);
    limitedMessages = messages.slice(-MAX_HISTORY);

    // Create a brief summary of older messages
    const oldUserMessages = oldMessages
      .filter(m => m.role === 'user' && m.content)
      .map(m => m.content!.substring(0, 100))
      .join(' | ');

    if (oldUserMessages) {
      historySummary = `[Previous conversation summary: ${oldUserMessages.substring(0, 300)}...]`;
    }
    console.log(`[GenContent] Limited history from ${messages.length} to ${limitedMessages.length} messages`);
  }

  // Add text-only versions of all messages (no images in history)
  // Start with history summary if we truncated
  if (historySummary) {
    apiContents.push({
      role: 'user',
      parts: [{ text: historySummary }]
    });
  }

  for (const msg of limitedMessages) {
    if (msg.content) {
      apiContents.push({
        role: msg.role === 'assistant' ? 'model' : 'user',
        parts: [{ text: msg.content }]
      });
    }
  }

  // Add visual anchor as a dedicated context message if available
  if (visualAnchor) {
    // Compress image to reduce payload size (Vercel has 4.5MB limit)
    let anchorBase64 = visualAnchor.base64;
    let anchorMimeType = visualAnchor.file.type || 'image/jpeg';

    // If image is larger than 500KB, compress it
    // If image is larger than 500KB, compress it
    if (anchorBase64.length > 500 * 1024) {
      try {
        console.log('[GenContent] Compressing large anchor image:', `${(anchorBase64.length / 1024).toFixed(1)}KB`);
        const compressed = await compressBase64Image(anchorBase64);
        anchorBase64 = compressed;
        anchorMimeType = 'image/jpeg';
        console.log('[GenContent] Compressed to:', `${(anchorBase64.length / 1024).toFixed(1)}KB`);
      } catch (e) {
        console.warn('[GenContent] Image compression failed, using original:', e);
      }
    }

    apiContents.push({
      role: 'user',
      parts: [
        { text: '[Visual Anchor - Last frame for continuity reference]' },
        {
          inlineData: {
            data: anchorBase64,
            mimeType: anchorMimeType
          }
        }
      ]
    });
  }

  // Log sanitized message summary for debugging
  const totalTextLength = apiContents.reduce((sum, msg) =>
    sum + msg.parts.reduce((s: number, p: any) => s + (p.text?.length || 0), 0), 0);
  const hasAnchorImage = !!visualAnchor;

  console.log('[GenContent] Sanitized messages:', {
    totalMessages: apiContents.length,
    textLength: totalTextLength,
    hasVisualAnchor: hasAnchorImage,
    anchorImageSize: visualAnchor ? `${(visualAnchor.base64.length / 1024).toFixed(1)}KB` : 'none'
  });

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO, // Ensure PRO model is used
    contents: apiContents,
    config: { systemInstruction }
  }, apiKey); // P0.6: Pass Local API Key

  const text = extractText(response);

  try {
    const jsonMatch = text.match(/```json\n?([\s\S]*?)\n?```/);
    const jsonStr = jsonMatch ? jsonMatch[1] : text;

    if (jsonStr.trim().startsWith('{')) {
      const parsed = JSON.parse(jsonStr);
      if (parsed.creativePrompt && parsed.veoOptimizedPrompt) return parsed;
    }
  } catch (e) {
    console.log("Parsing JSON failed, returning raw text", e);
  }

  return text;
};

/**
 * Streaming version of generateSequenceFromConversation
 * Uses SSE endpoint for progressive response display
 */
export const generateSequenceFromConversationStream = async (
  messages: ChatMessage[],
  dogma: Dogma | null,
  duration: number,
  onChunk: (text: string) => void,
  extensionContext?: ImageFile | null,
  motionDescription?: string | null,
  apiKey?: string,
  sequenceIntent?: 'plan-sequence' | 'decoupage' | 'infinite-chain' | null,
  additionalContext?: string,
  mentionedCharacters?: Character[],
  videoProvider?: VideoProvider
): Promise<string | { creativePrompt: string; veoOptimizedPrompt: string }> => {

  // Build system instruction (same logic as non-streaming version)
  let contextInstruction = extensionContext
    ? `User wants to EXTEND a video. Last frame provided.${motionDescription ? ` Continuity: "${motionDescription}"` : ''}`
    : `User wants a NEW video. Duration: ${duration}s.`;

  if (sequenceIntent === 'plan-sequence') {
    contextInstruction += `\n\n🎬 MODE: PLAN-SÉQUENCE (caméra continue, ${duration}s en un seul plan)`;
  } else if (sequenceIntent === 'decoupage') {
    contextInstruction += `\n\n🎬 MODE: DÉCOUPAGE (plans multiples, JSON Array output)`;
  }

  const systemInstruction = sequenceIntent === 'decoupage'
    ? `You are a Technical Director. Output ONLY a valid JSON Array of shot specs. No markdown.
${additionalContext || ''}`
    : `You are a creative VIDEO DIRECTOR assistant. Help craft the perfect video prompt.
When ready, output JSON: { "creativePrompt": "...", "veoOptimizedPrompt": "..." }
Dogma: ${dogma?.title || 'None'}${dogma?.text ? ` - ${dogma.text.substring(0, 200)}` : ''}
${contextInstruction}
${additionalContext || ''}`;

  // Build API contents with history limiting
  const MAX_HISTORY = 10;
  // History Limiting
  let limitedMessages = messages;
  let historySummary = '';

  if (messages.length > MAX_HISTORY) {
    const oldMessages = messages.slice(0, -MAX_HISTORY);
    limitedMessages = messages.slice(-MAX_HISTORY);
    const oldUserMessages = oldMessages
      .filter(m => m.role === 'user' && m.content)
      .map(m => m.content!.substring(0, 100))
      .join(' | ');

    if (oldUserMessages) {
      historySummary = `[Previous conversation summary: ${oldUserMessages.substring(0, 300)}...]`;
    }
  }

  const apiContents: any[] = [];

  // 1. Inject Mentioned Character Images (Fix 413: Compress!)
  if (mentionedCharacters && mentionedCharacters.length > 0) {
    console.log(`[Stream] Injecting visual references for ${mentionedCharacters.length} characters`);
    for (const char of mentionedCharacters) {
      if (char.images && char.images.length > 0) {
        apiContents.push({
          role: 'user',
          parts: [{ text: `REFERENCE CHARACTER: "${char.name}"\nDescription: ${char.description || 'See images.'}` }]
        });

        for (const img of char.images) {
          try {
            // COMPRESS
            const compressedBase64 = await compressBase64Image(img.base64);
            // We use inlineData directly as Streaming usually handles smaller payloads well if compressed
            apiContents.push({
              role: 'user',
              parts: [
                { text: `[${char.name}]` },
                { inlineData: { data: compressedBase64, mimeType: 'image/jpeg' } }
              ]
            });
          } catch (e) {
            console.warn(`[Stream] Failed to attach reference image for ${char.name}`, e);
          }
        }
      }
    }
  }

  // 2. Add History Summary
  if (historySummary) {
    apiContents.push({ role: 'user', parts: [{ text: historySummary }] });
  }

  // 3. Add Messages (Compress images in history if present)
  for (const msg of limitedMessages) {
    const parts: any[] = [{ text: msg.content || '' }];

    if (msg.image) {
      try {
        const compressed = await compressBase64Image(msg.image.base64);
        parts.push({
          inlineData: { data: compressed, mimeType: 'image/jpeg' }
        });
      } catch (e) {
        console.warn('[Stream] Warning: Could not compress message image', e);
      }
    }

    apiContents.push({
      role: msg.role === 'assistant' ? 'model' : 'user',
      parts: parts
    });
  }

  // 4. Inject Visual Anchor (Compress!)
  // Find visual anchor if not explicitly provided as extensionContext
  let visualAnchor: ImageFile | null = extensionContext || null;
  if (!visualAnchor) {
    // Find the most recent image in the conversation
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].image) {
        visualAnchor = messages[i].image;
        break;
      }
    }
  }

  if (visualAnchor) {
    try {
      const compressedAnchor = await compressBase64Image(visualAnchor.base64);
      apiContents.push({
        role: 'user',
        parts: [
          { text: '[Visual Anchor - Continuity Reference]' },
          { inlineData: { data: compressedAnchor, mimeType: 'image/jpeg' } }
        ]
      });
    } catch (e) {
      console.warn('[Stream] Failed to compress anchor', e);
    }
  }


  // Call streaming endpoint
  const fullText = await apiCallStream('/generate-content-stream', {
    model: MODELS.PRO,
    contents: apiContents,
    config: { systemInstruction }
  }, onChunk, apiKey);

  // Try to parse JSON result
  try {
    const jsonMatch = fullText.match(/```json\n?([\s\S]*?)\n?```/);
    const jsonStr = jsonMatch ? jsonMatch[1] : fullText;

    if (jsonStr.trim().startsWith('{') || jsonStr.trim().startsWith('[')) {
      const parsed = JSON.parse(jsonStr);
      if (parsed.creativePrompt && parsed.veoOptimizedPrompt) return parsed;
      if (Array.isArray(parsed)) return { creativePrompt: '', veoOptimizedPrompt: '', rawText: fullText } as any;
    }
  } catch (e) {
    console.log("[Stream] JSON parse failed, returning raw text");
  }

  return fullText;
};

// ===========================================
// IMAGE EDITING / GENERATION
// ===========================================

export const editImage = async (
  images: ImageFile[],
  prompt: string,
  dogma: Dogma | null,
  modelName: string = MODELS.IMAGE_FLASH,
  apiKey?: string,
  characters?: Character[] // Optional Character context
): Promise<ImageFile> => {
  // Use smart upload for large images
  const imageParts = await createImageParts(images);

  const dogmaInstruction = dogma?.text ? `\nDogma: ${dogma.text}` : '';

  let characterContext = '';
  if (characters && characters.length > 0) {
    const charList = characters.map((c: any) => `- Name: "${c.name}", Description: ${c.description || 'No description'}`).join('\n');
    characterContext = `\nAVAILABLE CHARACTERS (Refer to these visual descriptions if mentioned):\n${charList}`;
  }

  const finalPrompt = `Task: Edit image based on prompt. @image1 is target. Prompt: ${prompt}${dogmaInstruction}${characterContext}`;

  const response = await apiCall('/generate-content', {
    model: modelName,
    contents: {
      parts: [...imageParts, { text: finalPrompt }]
    }
  }, apiKey);

  const content = response.candidates?.[0]?.content;
  if (!content?.parts) throw new Error('No content');

  for (const part of content.parts) {
    if (part.inlineData) {
      const base64 = part.inlineData.data;
      const res = await fetch(`data:image/png;base64,${base64}`);
      const blob = await res.blob();
      const file = new File([blob], 'edited.png', { type: 'image/png' });

      // Optional: Upload to Supabase if configured
      if (isSupabaseConfigured()) {
        try {
          const supabaseUrl = await uploadImageToSupabase(blob, 'edited-image.png');
          console.log('Edited image uploaded to Supabase:', supabaseUrl);
        } catch (uploadError) {
          console.warn('Failed to upload image to Supabase:', uploadError);
        }
      }

      return { file, base64 };
    }
  }
  throw new Error('No image in response');
};

export const generateImageFromText = async (prompt: string, dogma: Dogma | null, apiKey?: string): Promise<ImageFile> => {
  const dogmaInstruction = dogma?.text ? `\nStyle: ${dogma.text}` : '';

  const response = await apiCall('/generate-content', {
    model: MODELS.IMAGE_PRO,
    contents: { parts: [{ text: prompt + dogmaInstruction }] }
  }, apiKey);

  const content = response.candidates?.[0]?.content;
  for (const part of content.parts) {
    if (part.inlineData) {
      const base64 = part.inlineData.data;
      const res = await fetch(`data:image/png;base64,${base64}`);
      const blob = await res.blob();

      // Optional: Upload to Supabase if configured
      if (isSupabaseConfigured()) {
        try {
          const supabaseUrl = await uploadImageToSupabase(blob, 'generated-image.png');
          console.log('Generated image uploaded to Supabase:', supabaseUrl);
        } catch (uploadError) {
          console.warn('Failed to upload image to Supabase:', uploadError);
        }
      }

      return { file: new File([blob], 'gen.png', { type: 'image/png' }), base64 };
    }
  }
  throw new Error('No image generated');
};

export const generateCharacterImage = async (
  prompt: string,
  contextImages: { file: File; base64: string }[],
  styleImage: ImageFile | null,
  apiKey?: string,
  aspectRatio: string = '2:3'
): Promise<ImageFile> => {
  const parts: any[] = [];

  // Use smart upload for large context images
  for (const img of contextImages) {
    const imageFile: ImageFile = { file: img.file, base64: img.base64 };
    const imagePart = await createImagePart(imageFile);
    parts.push(imagePart);
  }

  // Use smart upload for style image if provided
  if (styleImage) {
    const stylePart = await createImagePart(styleImage);
    parts.push(stylePart);
  }

  parts.push({ text: prompt });

  const response = await apiCall('/generate-content', {
    model: MODELS.IMAGE_PRO,
    contents: { parts },
    config: {
      generationConfig: {
        aspectRatio,
      }
    }
  }, apiKey);

  const content = response.candidates?.[0]?.content;
  for (const part of content.parts) {
    if (part.inlineData) {
      const base64 = part.inlineData.data;
      const res = await fetch(`data:image/png;base64,${base64}`);
      const blob = await res.blob();

      // Optional: Upload to Supabase if configured
      if (isSupabaseConfigured()) {
        try {
          const supabaseUrl = await uploadImageToSupabase(blob, 'character-image.png');
          console.log('Character image uploaded to Supabase:', supabaseUrl);
        } catch (uploadError) {
          console.warn('Failed to upload image to Supabase:', uploadError);
        }
      }

      return { file: new File([blob], 'char.png', { type: 'image/png' }), base64 };
    }
  }
  throw new Error('No image generated');
};

// ===========================================
// SPEECH (TTS)
// ===========================================

export const generateSpeech = async (text: string, voiceName: string, apiKey?: string): Promise<string> => {
  const response = await apiCall('/generate-content', {
    model: MODELS.TTS,
    contents: { parts: [{ text }] },
    config: {
      responseModalities: ['AUDIO'],
      speechConfig: {
        voiceConfig: { prebuiltVoiceConfig: { voiceName } }
      }
    }
  }, apiKey);

  const audioData = response.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
  if (!audioData) throw new Error("No audio data received");
  return audioData;
};

// ===========================================
// STORYBOARD GENERATION
// ===========================================

export const generateStoryboard = async (
  prompt: string,
  dogma: Dogma | null,
  referenceImages: ImageFile[],
  startFrame: ImageFile | null,
  endFrame: ImageFile | null,
  apiKey?: string
): Promise<Storyboard> => {
  const parts: any[] = [];

  // Use smart upload for large images
  if (startFrame) {
    const startPart = await createImagePart(startFrame);
    parts.push(startPart);
    parts.push({ text: "Start Frame" });
  }
  if (endFrame) {
    const endPart = await createImagePart(endFrame);
    parts.push(endPart);
    parts.push({ text: "End Frame" });
  }
  for (const img of referenceImages) {
    const refPart = await createImagePart(img);
    parts.push(refPart);
  }

  const systemPrompt = `Create 5-keyframe storyboard JSON for: "${prompt}". Dogma: ${dogma?.text}. Return JSON { "prompt": "", "keyframes": [{ "timestamp": "", "description": "", "imageBase64": "PLACEHOLDER" }] }`;
  parts.push({ text: systemPrompt });

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: { parts },
    config: { responseMimeType: 'application/json' }
  }, apiKey);

  const text = extractText(response);
  const storyboard = JSON.parse(text);

  // Parallel generation for images
  const imagePromises = storyboard.keyframes.map(async (kf: any) => {
    try {
      const img = await generateImageFromText(kf.description, dogma, apiKey);
      return { ...kf, imageBase64: img.base64 };
    } catch (e) {
      return kf;
    }
  });

  storyboard.keyframes = await Promise.all(imagePromises);
  return storyboard;
};

// ===========================================
// DECOUPAGE PRO SERVICES
// ===========================================

/**
 * Generate a structured decoupage (shot list) from a master image and prompt
 * Uses Gemini 1.5 Pro to analyze the image and breakdown the scene.
 */
export const generateDecoupageFromPrompt = async (
  masterImageBase64: string,
  userPrompt: string,
  context?: string
): Promise<any> => {
  const systemPrompt = `You are a professional cinematographer assistant.
  Analyze the provided Master Shot image.
  Based on the user's request: "${userPrompt}", create a breakdown of 4-6 specific shots (Vignettes) that explore this scene.
  
  CRITICAL:
  1. Maintain strict visual consistency with the Master Shot.
  2. For "nano_prompt", describe the shot vividly, explicitly mentioning visual details seen in the master image (colors, lighting, clothes).
  3. Return STRICT JSON matching this schema:
  {
    "shots": [
      {
        "type": "CLOSE_UP" | "INSERT" | "MEDIUM_SHOT" | "WIDE",
        "label": "Short Title",
        "nano_prompt": "Detailed visual description...",
        "suggested_duration": number (2-5)
      }
    ]
  }
  Do not include markdown formatting like \`\`\`json. Return raw JSON only.`;

  try {
    const parts: any[] = [{ text: systemPrompt }];

    // Add Master Image
    // Ensure we strip header if present
    const cleanBase64 = masterImageBase64.replace(/^data:image\/\w+;base64,/, '');

    parts.push({
      inlineData: {
        mimeType: 'image/jpeg',
        data: cleanBase64
      }
    });

    const result = await apiCall('/generate-content', {
      model: 'gemini-3.0-pro-001', // Explicitly requested SOTA model
      contents: [{ role: 'user', parts }],
      config: {
        temperature: 0.7,
        responseMimeType: 'application/json'
      }
    });

    const text = extractText(result);
    // Parse JSON safely
    try {
      const parsed = JSON.parse(text);
      return parsed.shots || parsed;
    } catch (e) {
      console.warn('[Decoupage] JSON Parse failed, trying cleanup:', e);
      const cleanText = text.replace(/```json\n?|\n?```/g, '');
      return JSON.parse(cleanText).shots;
    }

  } catch (error) {
    console.error('[Decoupage] Generation failed:', error);
    throw error;
  }
};

// ===========================================
// UTILITIES
// ===========================================

export const toggleTranslateText = async (text: string, apiKey?: string): Promise<string> => {
  const response = await apiCall('/generate-content', {
    model: MODELS.FLASH,
    contents: `Translate to English if French, or French if English: "${text}"`
  }, apiKey);
  const result = extractText(response);
  return result.trim();
};

export const regenerateSinglePrompt = async (
  params: RegeneratePromptParams & {
    dogma: Dogma | null;
    promptBefore?: string;
    promptAfter?: string;
  },
  apiKey?: string
): Promise<string> => {
  const { instruction, promptToRevise, dogma, promptBefore, promptAfter } = params;

  // DEBUG: Log which dogma is being used
  console.log('[DogmaDebug] regenerateSinglePrompt called with dogma:', {
    id: dogma?.id || 'none',
    title: dogma?.title || 'none',
    textPreview: dogma?.text?.substring(0, 100) + '...' || 'none'
  });

  const systemInstruction = `Revise ONE prompt in a sequence. Dogma: ${dogma?.text}.
  Context: Before: "${promptBefore}", After: "${promptAfter}".
  Target: "${promptToRevise}". User Change: "${instruction}".
  Output JSON: { "revisedPrompt": "..." }`;

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: { parts: [{ text: "Revise." }] },
    config: {
      systemInstruction,
      responseMimeType: 'application/json'
    }
  });
  const text = extractText(response);
  const res = JSON.parse(text);
  return res.revisedPrompt;
};

export const reviseFollowingPrompts = async (
  params: ReviseFollowingPromptsParams,
  apiKey?: string
): Promise<string[]> => {
  const { dogma, promptBefore, editedPrompt, promptsToRevise } = params;
  const systemInstruction = `Continuity Editor. Dogma: ${dogma?.text}.
  Before: ${promptBefore}. EDITED: ${editedPrompt}.
  Old Next Prompts: ${JSON.stringify(promptsToRevise)}.
  Rewrite Next Prompts for continuity. Output JSON: { "revisedPrompts": ["..."] }`;

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: { parts: [{ text: "Fix continuity." }] },
    config: {
      systemInstruction,
      responseMimeType: 'application/json'
    }
  });

  const text = extractText(response);
  const res = JSON.parse(text);
  return res.revisedPrompts || promptsToRevise;
};

export const getRevisionAssistant = async () => {
  return null;
}

export const getRevisionAssistantResponse = async (params: any) => {
  // DEBUG: Log which dogma is being used for revision
  console.log('[DogmaDebug] getRevisionAssistantResponse called with dogma:', {
    id: params.dogma?.id || 'none',
    title: params.dogma?.title || 'none',
    textPreview: params.dogma?.text?.substring(0, 100) + '...' || 'none'
  });

  // Build rich context from available characters and scene history
  let contextParts = [];

  if (params.characters && params.characters.length > 0) {
    const charList = params.characters.map((c: any) => `- Name: "${c.name}", Description: ${c.description || 'No description'}`).join('\n');
    contextParts.push(`AVAILABLE CHARACTERS (The user may mention them by name):\n${charList}`);
  }

  if (params.promptBefore) {
    contextParts.push(`PREVIOUS SHOT PROMPT: "${params.promptBefore}"`);
  }

  if (params.promptAfter) {
    contextParts.push(`NEXT SHOT PROMPT: "${params.promptAfter}"`);
  }

  contextParts.push(`TASK: REVISE the current prompt based on user feedback. Maintain consistency with the characters and dogma.`);

  const additionalContext = contextParts.join('\n\n');

  const res = await generateSequenceFromConversation(
    params.messages,
    params.dogma,
    0, // Duration irrelevant for text-only revision
    undefined, // No extension context image
    undefined, // No motion
    undefined, // API Key
    undefined, // Sequence Intent
    additionalContext // <--- Inject Context Here
  );
  if (typeof res === 'string') return res;
  return { isFinalRevision: true, revisedPrompt: res.veoOptimizedPrompt || res.creativePrompt };
};

// ===========================================
// ADVANCED FEATURES
// ===========================================

export const generateDogmaFromMedia = async (
  mediaFile: File,
  mediaBase64: string
): Promise<Omit<Dogma, 'id'>> => {
  const systemPrompt = `You are an expert Art Director. Analyze this media (image or video frame) and extract its "Visual DNA" to create a Dogma for our video generation studio.
  Identify:
  1. Visual Rules (Lighting, Color Palette, Texture, Composition).
  2. Camera Movement style.
  3. Negative Prompts (what to avoid to maintain this style).

  Output JSON ONLY: { "title": "Name of Style", "text": "Detailed markdown instructions...", "referenceImages": [] }
  For the text field, use the structure: ### RULES, ### LIGHTING, ### CAMERA, ### NEGATIVE PROMPT.`;

  // Use smart upload for large media files
  const imageFile: ImageFile = { file: mediaFile, base64: mediaBase64 };
  const mediaPart = await createImagePart(imageFile);

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: {
      parts: [
        mediaPart,
        { text: "Extract DNA." }
      ]
    },
    config: {
      systemInstruction: systemPrompt,
      responseMimeType: 'application/json'
    }
  });

  const text = extractText(response);
  const res = JSON.parse(text);
  return {
    title: res.title || "Extracted Style",
    text: res.text || "No description generated.",
    referenceImages: []
  };
};

export const analyzeVideoCompliance = async (
  frameBase64: string,
  prompt: string,
  dogma: Dogma | null
): Promise<ComplianceResult> => {
  const dogmaText = dogma?.text || "Standard Cinematic Rules";
  const systemPrompt = `You are the "Critic Agent". Your job is to compare the generated video frame against the User Prompt and the active Dogma.
    Dogma: ${dogmaText}
    Prompt: ${prompt}

    Analyze the image. Does it respect the lighting, style, and content requested?
    Output JSON: { "score": number (0-100), "critique": "Short constructive critique (max 2 sentences).", "revisedPrompt": "An improved version of the prompt to fix the issues (optional)." }`;

  // Create a dummy ImageFile for smart upload handling
  const estimatedSize = frameBase64.length * 0.75;
  let imagePart: any;

  if (estimatedSize > LARGE_FILE_THRESHOLD) {
    // Large frame: Upload to Google Files API first
    console.log(`[SmartUpload] Frame is large (${(estimatedSize / 1024 / 1024).toFixed(2)}MB), using Google Files API`);
    const response = await fetch(`data:image/jpeg;base64,${frameBase64}`);
    const blob = await response.blob();
    const uploadResult = await uploadToGoogleFiles(blob, `frame-${Date.now()}.jpg`);
    imagePart = {
      fileData: {
        fileUri: uploadResult.fileUri,
        mimeType: 'image/jpeg',
      }
    };
  } else {
    // Small frame: Use inline base64
    imagePart = {
      inlineData: { data: frameBase64, mimeType: 'image/jpeg' }
    };
  }

  const response = await apiCall('/generate-content', {
    model: MODELS.PRO,
    contents: {
      parts: [
        imagePart,
        { text: "Critique this." }
      ]
    },
    config: {
      systemInstruction: systemPrompt,
      responseMimeType: 'application/json'
    }
  });

  const text = extractText(response);
  return JSON.parse(text);
};
